import React, { useEffect, useMemo } from 'react';
import c from 'classnames';
import type { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

import {
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_DAYS_PER_WEEK,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_DAYS_PER_WEEK_RESULT,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_HIDDEN_FIELD,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_HOURS_PER_DAY,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_HOURS_PER_DAY_RESULT,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_SOUND_VOLUME,
  WEBFORM_KEY_MUSIC_PLAYER_CHECK_SOUND_VOLUME_RESULT,
} from '../../../../config/constants';

import './style.scss';

function log10(value: number) {
  return Math.log(value) / Math.LN10;
}

function mapPercentageToDecibel(percentage: number): number {
  switch (percentage) {
    case 100:
      return 100;
    case 90:
      return 95;
    case 80:
      return 90;
    case 70:
      return 85;
    case 60:
      return 80;
    case 50:
      return 75;
    case 40:
      return 69;
    case 30:
      return 63;
    case 20:
      return 57;
    case 10:
      return 51;
    case 0:
    default:
      return 45;
  }
}

function calculateResult(daysPerWeek: number, hoursPerDay: number, soundVolume: number) {
  const soundLevel = mapPercentageToDecibel(soundVolume);
  const soundDose = soundLevel - 10 * log10(8 / hoursPerDay) - 10 * log10(5 / daysPerWeek) - 10 * log10(4 / 4);

  if (soundDose <= 88) {
    return 'success';
  }

  if (soundDose > 88 && soundDose <= 92.5) {
    return 'warning';
  }

  return 'error';
}

interface ExtendedFormikContext extends FormikProps<{ [key: string]: string }> {}

const WebformCustomMusicPlayerCheck: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.webform.custom.music_player_check' });
  const { values, setFieldValue } = useExtendedFormikContext() as ExtendedFormikContext;

  const result = useMemo(() => {
    const daysPerWeek = values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_DAYS_PER_WEEK_RESULT];
    const hoursPerDay = values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_HOURS_PER_DAY_RESULT];
    const soundVolume = values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_SOUND_VOLUME_RESULT];

    if (!(daysPerWeek && hoursPerDay && soundVolume)) {
      return undefined;
    }

    return calculateResult(Number(daysPerWeek), Number(hoursPerDay), Number(soundVolume));
  }, [values]);

  useEffect(() => {
    if (result) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      setFieldValue(WEBFORM_KEY_MUSIC_PLAYER_CHECK_HIDDEN_FIELD, t(`result_label_${result}`));
    }
  }, [result, setFieldValue, t]);

  // Each dynamic music player check form element has a corresponding element on
  // the result page. Here we give these elements a default value from the original element.
  useEffect(() => {
    const defaultValues = {
      [WEBFORM_KEY_MUSIC_PLAYER_CHECK_DAYS_PER_WEEK_RESULT]: values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_DAYS_PER_WEEK],
      [WEBFORM_KEY_MUSIC_PLAYER_CHECK_HOURS_PER_DAY_RESULT]: values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_HOURS_PER_DAY],
      [WEBFORM_KEY_MUSIC_PLAYER_CHECK_SOUND_VOLUME_RESULT]: values[WEBFORM_KEY_MUSIC_PLAYER_CHECK_SOUND_VOLUME],
    };

    Object.entries(defaultValues).forEach(([key, value]) => {
      if (value) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        setFieldValue(key, value);
      }
    });
  // We only want to do this on initial load of the component.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFieldValue]);

  const classes = c('WebformCustomMusicPlayerCheck', {
    [`WebformCustomMusicPlayerCheck--result--${String(result)}`]: result !== undefined,
    [`u-background--system--${String(result)}--400`]: result !== undefined,
  });

  if (!result) {
    return null;
  }

  return (
    <div className={classes}>
      <span className="WebformCustomMusicPlayerCheck__label">
        {t('label')}
      </span>

      <span className="WebformCustomMusicPlayerCheck__result-label">
        {t(`result_label_${result}`)}
      </span>
    </div>
  );
};

export default WebformCustomMusicPlayerCheck;
